import {
	BlueInputConfigValues,
	BlueInputContext,
	defaultBlueInputConfig,
} from '@eag/sales-app-base/build/app/library/components/form/BlueInputContext';
import type { TimeUnit } from 'chart.js';
import { ActiveElement, Chart, Point, Tooltip, TooltipPosition, _adapters } from 'chart.js';
import 'chartjs-adapter-dayjs-3';
import Annotation from 'chartjs-plugin-annotation';
import dayjs from 'dayjs';
import 'dayjs/locale/de-at';
import AdvancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import updateLocale from 'dayjs/plugin/updateLocale';
import de_AT from 'primelocale/de-AT.json';
import { addLocale, locale } from 'primereact/api';
import 'primereact/resources/primereact.min.css';
import React, { lazy } from 'react';
import { createRoot } from 'react-dom/client';

import { ProductSuggestionModel } from '@eag/sales-app-base/build/app/core/rest-api/model';
import FooterApp from 'apps/FooterApp/FooterApp';
import KontaktFloaterApp from 'apps/KontaktFloaterApp/KontaktFloaterApp';
import StandaloneComponent from 'apps/LoginPageContentsComponent/StandaloneComponent';
import NavigationApp from 'apps/NavigationApp/NavigationApp';
import {
	AbstractContentModel,
	FilterFaqAppConfig,
	GlobalConfig,
	NavigationPreloadData,
	TeaserPreloadData,
	WechselAppConfig,
} from 'interfaces/interfaces';
import 'sigi-assets/assets/css/bc-apps.css';

import './i18n';

dayjs.extend(updateLocale);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(AdvancedFormat);
dayjs.extend(quarterOfYear);
dayjs.extend(localizedFormat);
dayjs.extend(objectSupport);
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.locale('de-at');
dayjs.updateLocale('de-at', {
	monthsShort: ['Jän', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
});

Chart.register(Annotation);
Chart.defaults.locale = 'de-at';
Chart.defaults.font.family = import.meta.env.VITE_CHART_DEFAULT_FONT;
(Tooltip.positioners as any).cursor = (_items: readonly ActiveElement[], eventPosition: Point): TooltipPosition => {
	return {
		x: eventPosition.x,
		y: eventPosition.y,
	};
};

_adapters._date.override({
	parse: function (value: any, format?: TimeUnit) {
		const valueType = typeof value;

		if (value === null || valueType === 'undefined') {
			return null;
		}

		if (valueType === 'object' && '$d' in value) {
			return dayjs(new Date(value.$d)).valueOf();
		}

		if (valueType === 'string' && typeof format === 'string') {
			return dayjs(value, format).isValid() ? dayjs(value, format).valueOf() : null;
		} else if (!(value instanceof dayjs)) {
			return dayjs(value).isValid() ? dayjs(value).valueOf() : null;
		}
		return null;
	},
});

addLocale('de-AT', {
	...de_AT['de-AT'],
});
locale('de-AT');

declare global {
	interface Window {
		gruenstrom: any;
		dataLayer: any;
		_paq: any;
		SG: {
			container: {
				deliveryInfo: {
					defaultActionSuffix: string;
				};
			};
		};
		ppms: any;
	}
}

interface GruenstromGlobal {
	react: {
		produkte?: (
			appId: string,
			cfg: GlobalConfig,
			preloadData: [AbstractContentModel, { products: ProductSuggestionModel[] }]
		) => void;
		produkt?: (
			appId: string,
			cfg: GlobalConfig & { productId?: string },
			preloadData: [AbstractContentModel, { products: ProductSuggestionModel[] }]
		) => void;
		navigation?: (appId: string, cfg: GlobalConfig, preloadData: NavigationPreloadData[]) => void;
		footer?: (appId: string) => void;
		spotPreise?: (appId: string, cfg: GlobalConfig, preloadData: [AbstractContentModel]) => void;
		spotPreiseDownload?: (appId: string, cfg: GlobalConfig, preloadData: [AbstractContentModel]) => void;
		kontaktFloater?: (appId: string) => void;
		sliderBuehne?: (appId: string, cfg: GlobalConfig, preloadData: TeaserPreloadData[]) => void;
		wechselSchritte?: (
			appId: string,
			cfg: WechselAppConfig,
			preloadData: [AbstractContentModel, TeaserPreloadData]
		) => void;
		filterFaq: (appId: string, cfg: FilterFaqAppConfig, preloadData: [AbstractContentModel]) => void;
		floater?: (appId: string, preloadData: [AbstractContentModel]) => void;
		kontaktFormular?: (appId: string, cfg: GlobalConfig, preloadData: [AbstractContentModel]) => void;
		widerruf?: (appId: string, cfg: GlobalConfig, preloadData: [AbstractContentModel]) => void;
	};
}

const inputConfig: BlueInputConfigValues = {
	...defaultBlueInputConfig,
	maskSlotChar: '',
	truncateInfoText: true,
	labelPosition: 'beforeInput',
	helpTootipButtonClassName: 'button naked info icon-only help-tooltip-button',
};

const gruenstrom: GruenstromGlobal = window.gruenstrom || {};
gruenstrom.react = {
	produkte: (
		appId: string,
		cfg: GlobalConfig,
		preloadData: [AbstractContentModel, { products: ProductSuggestionModel[] }]
	) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const ProdukteApp = lazy(() => import('./apps/ProdukteApp/ProdukteApp'));
		root.render(
			<React.StrictMode>
				<ProdukteApp article={preloadData?.[0] || {}} products={preloadData?.[1]?.products || []} />
			</React.StrictMode>
		);
	},
	produkt: (
		appId: string,
		cfg: GlobalConfig & { productId?: string },
		preloadData: [AbstractContentModel, { products: ProductSuggestionModel[] }]
	) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const ProduktApp = lazy(() => import('./apps/ProduktApp/ProduktApp'));
		root.render(
			<React.StrictMode>
				<ProduktApp
					article={preloadData?.[0] || {}}
					products={preloadData?.[1]?.products || []}
					productId={cfg.productId}
				/>
			</React.StrictMode>
		);
	},
	navigation: (appId: string, cfg: GlobalConfig, preloadData: NavigationPreloadData[]) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		root.render(
			<React.StrictMode>
				<NavigationApp cfg={cfg} preloadData={preloadData} />
			</React.StrictMode>
		);
	},
	footer: (appId: string) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		root.render(
			<React.StrictMode>
				<FooterApp />
			</React.StrictMode>
		);
	},
	spotPreise: (appId: string, cfg: GlobalConfig, preloadData: [AbstractContentModel]) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const SpotPreiseApp = lazy(() => import('apps/SpotPreiseApp/SpotPreiseApp'));
		root.render(
			<React.StrictMode>
				<BlueInputContext.Provider value={inputConfig}>
					<SpotPreiseApp appId={appId} article={preloadData?.[0] || {}} />
				</BlueInputContext.Provider>
			</React.StrictMode>
		);
	},
	spotPreiseDownload: (appId: string, cfg: GlobalConfig, preloadData: [AbstractContentModel]) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const SpotPreiseDownloadApp = lazy(() => import('apps/SpotPreiseDownloadApp/SpotPreiseDownloadApp'));
		root.render(
			<React.StrictMode>
				<SpotPreiseDownloadApp article={preloadData?.[0] || {}} />
			</React.StrictMode>
		);
	},
	kontaktFloater: (appId: string) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		root.render(
			<React.StrictMode>
				<KontaktFloaterApp />
			</React.StrictMode>
		);
	},
	sliderBuehne: (appId: string, cfg: GlobalConfig, preloadData: TeaserPreloadData[]) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const SliderBuehneApp = lazy(() => import('apps/SliderBuehneApp/SliderBuehneApp'));
		root.render(
			<React.StrictMode>
				<SliderBuehneApp articles={preloadData?.[0]?.result || []} />
			</React.StrictMode>
		);
	},
	wechselSchritte: (appId: string, cfg: WechselAppConfig, preloadData: [AbstractContentModel, TeaserPreloadData]) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const WechselSchritteApp = lazy(() => import('apps/WechselSchritteApp/WechselSchritteApp'));
		root.render(
			<React.StrictMode>
				<WechselSchritteApp cfg={cfg} article={preloadData?.[0]} subArticles={preloadData?.[1]?.result || []} />
			</React.StrictMode>
		);
	},
	filterFaq: (appId: string, cfg: FilterFaqAppConfig, preloadData: [AbstractContentModel]) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const FilterFaqApp = lazy(() => import('apps/FilterFaqApp/FilterFaqApp'));
		root.render(
			<React.StrictMode>
				<FilterFaqApp cfg={cfg} article={preloadData?.[0]} />
			</React.StrictMode>
		);
	},
	floater: (appId: string, preloadData: [AbstractContentModel]) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const FloaterApp = lazy(() => import('apps/FloaterApp/FloaterApp'));
		root.render(
			<React.StrictMode>
				<FloaterApp article={preloadData?.[0]} />
			</React.StrictMode>
		);
	},
	kontaktFormular: (appId: string, cfg: GlobalConfig, preloadData: [AbstractContentModel]) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const KontaktFormularApp = lazy(() => import('apps/KontaktFormularApp/KontaktFormularApp'));
		root.render(
			<React.StrictMode>
				<KontaktFormularApp article={preloadData?.[0]} />
			</React.StrictMode>
		);
	},
	widerruf: (appId: string, cfg: GlobalConfig, preloadData: [AbstractContentModel]) => {
		const container = document.getElementById('reactRoot' + appId);
		const root = createRoot(container!);
		const WiderrufApp = lazy(() => import('apps/WiderrufApp/WiderrufApp'));
		root.render(
			<React.StrictMode>
				<WiderrufApp article={preloadData?.[0]} />
			</React.StrictMode>
		);
	}
};

window.customElements.get('login-page-contents-component') ??
	window.customElements.define('login-page-contents-component', StandaloneComponent);
